import type { CommentTag, CommentTagCatalogue } from '@engagement-web-app/common';
import { SlidersHorizontal } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import * as styles from './index.css';

type TagFilterProps = {
    catalogue: CommentTagCatalogue | null;
    selectedTagIds: readonly string[];
    onSelectedTagIdsChange: (ids: string[]) => void;
};

// FIXME: This is temporal implementation as the filter dialog's visual design is not ready atm.
export function TagFilter({ catalogue, selectedTagIds, onSelectedTagIdsChange }: TagFilterProps) {
    const [isOpen, setIsOpen] = useState(false);

    // Bucket tags by their group id. Ungrouped tags live under the seeded
    // 'UNGROUPED' group, so they render through the normal group loop below.
    const tagsByBucket = useMemo(() => {
        const buckets = new Map<string, CommentTag[]>();
        for (const tag of catalogue?.tags ?? []) {
            const arr = buckets.get(tag.groupId) ?? [];
            arr.push(tag);
            buckets.set(tag.groupId, arr);
        }
        return buckets;
    }, [catalogue]);

    const toggleTag = (id: string) => {
        onSelectedTagIdsChange(
            selectedTagIds.includes(id) ? selectedTagIds.filter((i) => i !== id) : [...selectedTagIds, id]
        );
    };

    const clearAll = () => onSelectedTagIdsChange([]);

    const selectedCount = selectedTagIds.length;

    return (
        <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenu.Trigger asChild>
                <Button variant="secondary">
                    <Icon icon={SlidersHorizontal} size="sm" />
                    絞り込み{selectedCount > 0 ? ` (${selectedCount})` : ''}
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content className={styles.content} align="start" sideOffset={4}>
                    <Flex justify="space-between" align="center" className={styles.header}>
                        <div className={styles.headerTitle}>タグで絞り込み</div>
                        {selectedCount > 0 && (
                            <button type="button" className={styles.clearButton} onClick={clearAll}>
                                クリア
                            </button>
                        )}
                    </Flex>

                    {(catalogue?.groups ?? []).map((group) => {
                        const groupTags = tagsByBucket.get(group.id) ?? [];
                        if (groupTags.length === 0) return null;
                        return (
                            <Flex key={group.id} direction="column" gap="1">
                                <div className={styles.groupLabel}>{group.name}</div>
                                {groupTags.map((tag) => (
                                    <DropdownMenu.CheckboxItem
                                        key={tag.id}
                                        className={styles.checkboxItem}
                                        checked={selectedTagIds.includes(tag.id)}
                                        onCheckedChange={() => toggleTag(tag.id)}
                                        onSelect={(e) => e.preventDefault()}
                                    >
                                        <Checkbox checked={selectedTagIds.includes(tag.id)} />
                                        {tag.name}
                                    </DropdownMenu.CheckboxItem>
                                ))}
                            </Flex>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
